"use client";

import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import useSavedCards from "@/components/pages/account/saved-cards/hooks/useSavedCards";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/input";
import Checkbox from "@/components/ui/input/Checkbox";
import PhoneNumber from "@/components/ui/input/PhoneNumber";
import { Link } from "@/i18n/navigation";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import CardForm from "./CardForm";

const createSchema = (t) =>
  z.object({
    cardName: z.string().min(1, t("common.cardNameRequired")),
    phoneNumber: z.string().min(1, t("common.phoneNumberRequired")),
  });

/**
 * SavedCardDetailModal
 * Handles both creating new cards and updating existing ones.
 * Uses react-hook-form with zod validation.
 */
export default function SavedCardDetailModal({
  card,
  onSubmit,
  submitLabel = "Güncelle",
}) {
  const [agreement, setAgreement] = useState(false);
  const [agreementError, setAgreementError] = useState(false);
  const t = useTranslationDetector();
  const { cardProgram, getBinDetails } = useSavedCards();

  const formRef = useRef(null);
  const ccFormRef = useRef(null);

  const isEditMode = Boolean(card);

  const schema = useMemo(() => createSchema(t), [t]);

  const defaultValues = useMemo(
    () => ({
      cardName: card?.CardName || "",
      phoneNumber: card?.PhoneNumber || "",
    }),
    [card?.CardName, card?.PhoneNumber],
  );

  const initialCardValues = useMemo(
    () =>
      card
        ? {
            cardNumber: card.MaskedCardNumber,
            expiry: "**/***",
            cvv: "***",
            name: "",
          }
        : null,
    [card],
  );

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const buildPayload = useCallback(
    (formData, ccValues) => {
      const basePayload = {
        CardName: formData.cardName,
      };

      if (isEditMode) {
        return {
          ...basePayload,
          CardId: card.Id,
          CardToken: card.CardToken,
        };
      }

      return {
        ...basePayload,
        CardNumber: ccValues?.CardNumber,
        ExpiryMonth: ccValues?.ExpirationMonth,
        ExpiryYear: ccValues?.ExpirationYear,
        PhoneNumber: formData.phoneNumber?.replaceAll("-", "") || "",
        CardHolderName: " ",
      };
    },
    [isEditMode, card?.Id, card?.CardToken],
  );

  const handleCreditCardSubmit = useCallback(
    async (ccValues) => {
      const formData = getValues();
      const payload = buildPayload(formData, ccValues);
      await onSubmit?.(payload);
    },
    [getValues, buildPayload, onSubmit],
  );

  const onFormSubmit = useCallback(
    async (formData) => {
      if (agreementError) {
        return;
      }

      if (!isEditMode) {
        ccFormRef.current?.submit();
        return;
      }

      const payload = buildPayload(formData, null);
      await onSubmit?.(payload);
    },
    [isEditMode, agreementError],
  );

  const handleFormValidation = useCallback(
    (event) => {
      event.preventDefault();

      if (!isEditMode) {
        if (!agreement) {
          setAgreementError(true);
          return;
        }
        ccFormRef.current?.validate();
      }

      handleSubmit(onFormSubmit)(event);
    },
    [isEditMode, agreement],
  );

  const handleButtonClick = useCallback(() => {
    formRef.current?.requestSubmit();
  }, []);

  return (
    <div className="flex flex-col gap-4 overflow-x-visible">
      <form
        ref={formRef}
        onSubmit={handleFormValidation}
        className="flex flex-col gap-4"
      >
        <div>
          <Input
            label={t("common.cardName") || "Kart Adı"}
            id="cardName"
            type="text"
            className="w-full"
            {...register("cardName")}
            error={errors.cardName?.message}
            required
          />
        </div>

        <div className="relative z-10">
          <Controller
            name="phoneNumber"
            control={control}
            render={({ field }) => (
              <PhoneNumber
                name="phoneNumber"
                value={field.value || null}
                defaultCountry="TR"
                labels={{
                  country: t("common.country"),
                  phone: t("common.phone"),
                }}
                placeholders={{
                  phone: t("common.phonePlaceholder"),
                }}
                onChange={(combined) => setValue("phoneNumber", combined)}
                error={errors.phoneNumber?.message}
                required
                disabled={isEditMode}
              />
            )}
          />
        </div>
      </form>

      <CardForm
        ref={ccFormRef}
        value={initialCardValues}
        onSubmit={handleCreditCardSubmit}
        showCardHolder={false}
        cardProgramImage={cardProgram?.CardType}
        onUnmaskedLengthMatch={getBinDetails}
        disabled={isEditMode}
      />

      <div className="pt-2">
        {isEditMode ? (
          <div className="text-primary-muted-subtext text-sm">
            {t("savedCards.onlyCardNameText")}
          </div>
        ) : (
          <>
            <div className="flex items-start justify-start">
              <Checkbox
                id="agreement"
                checked={!!agreement}
                onChange={(e) => {
                  setAgreementError(false);
                  setAgreement(e.target.checked);
                }}
              ></Checkbox>

              <span className="text-primary-muted-subtext text-sm">
                {t.rich("savedCards.mobilExpressAgreement", {
                  agreement: (chunks) => (
                    <Link
                      href={t("savedCards.mobilExpressLink")}
                      className="underline"
                      target="_blank"
                    >
                      {chunks}
                    </Link>
                  ),
                })}
              </span>
            </div>

            {agreementError && (
              <p className="text-danger flex items-center gap-1 pt-1 pl-4 text-xs">
                <FontAwesomeIcon
                  icon={byPrefixAndName.far["circle-exclamation"]}
                />
                <span>{t("savedCards.agreementRequired")}</span>
              </p>
            )}
          </>
        )}
      </div>

      <div className="pt-2">
        <Button
          className="w-full"
          color="primary"
          variant="solid"
          type="button"
          disabled={isSubmitting}
          onClick={handleButtonClick}
        >
          {submitLabel}
        </Button>
      </div>
    </div>
  );
}
